import React, { useState, useEffect, useCallback } from 'react';
import { X, Phone, Home, Baby, CalendarDays, FileSignature, Download, Save, Loader, Mail, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { preinscriptionsApi, enfantsApi, documentsModelesApi } from '../../utils/api';
import SignatureButton from '../../components/Signature/SignatureButton';
import EnvoiDocumentButton from '../../components/EnvoiDocumentButton';
import { openPdf } from '../../components/PdfPreview';

const STATUTS = [
  ['demande', 'Demande'], ['appel', 'Appelé'], ['devis', 'Devis envoyé'], ['visite', 'Visite faite'],
  ['devis_signe', 'Devis signé'], ['convention', 'Convention'], ['finalise', 'Finalisé'], ['annule', 'Annulé'],
];
const CONVENTION_CLE = '01-convention-reservation';

const Section = ({ icon: Icon, title, children, right }) => (
  <div className="rounded-xl border border-cream-300 p-4">
    <div className="flex items-center justify-between mb-3">
      <h4 className="flex items-center gap-2 font-semibold text-navy-800 text-sm"><Icon size={16} className="text-navy-500" /> {title}</h4>
      {right}
    </div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <label className="text-xs text-navy-600 block">{label}<div className="mt-1">{children}</div></label>
);

export default function PreinscriptionDossier({ preinscriptionId, onClose, onSaved }) {
  const [pre, setPre] = useState(null);
  const [enfant, setEnfant] = useState(null);
  const [p1, setP1] = useState({});
  const [p2, setP2] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [mails, setMails] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: pr } = await preinscriptionsApi.getById(preinscriptionId);
      setPre(pr);
      if (pr.enfant_id) {
        try {
          const { data: e } = await enfantsApi.getById(pr.enfant_id);
          setEnfant(e);
          setP1((e.parents || []).find(x => x.type === 'parent1') || { type: 'parent1', lien: 'Mère' });
          setP2((e.parents || []).find(x => x.type === 'parent2') || { type: 'parent2', lien: 'Père' });
        } catch { /* enfant inaccessible */ }
        enfantsApi.getEmails(pr.enfant_id).then(({ data }) => setMails(data)).catch(() => setMails(null));
      }
    } finally { setLoading(false); }
  }, [preinscriptionId]);
  useEffect(() => { load(); }, [load]);

  const updPre = (k, v) => setPre(p => ({ ...p, [k]: v }));
  const updE = (k, v) => setEnfant(e => ({ ...e, [k]: v }));

  const saveAll = async () => {
    setSaving(true); setMsg('');
    try {
      // 1) suivi pré-inscription (dates, notes appel/visite, jours, statut)
      await preinscriptionsApi.update(pre.id, pre);
      // 2) fiche enfant + parents (si un enfant est rattaché)
      if (enfant?.id) {
        await enfantsApi.update(enfant.id, {
          enfant: {
            structure_id: enfant.structure_id || pre.structure_id,
            nom: enfant.nom, prenom: enfant.prenom, date_naissance: enfant.date_naissance,
            sexe: enfant.sexe, adresse: enfant.adresse, medecin_nom: enfant.medecin_nom,
            medecin_tel: enfant.medecin_tel, allergies: enfant.allergies, antecedents: enfant.antecedents,
            pai: enfant.pai, statut: enfant.statut, date_entree_prevue: enfant.date_entree_prevue,
          },
          parent1: p1, parent2: p2,
        });
      }
      setMsg('✓ Enregistré');
      onSaved?.();
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('Erreur : ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const telechargerConvention = () => {
    if (!enfant?.id) return;
    openPdf(documentsModelesApi.getPdfUrl(enfant.id, [CONVENTION_CLE]));
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-cream-50 rounded-2xl shadow-xl w-full max-w-3xl my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-cream-300 bg-white rounded-t-2xl sticky top-0 z-10">
          <div>
            <h3 className="font-serif text-lg text-navy-900">Dossier de pré-inscription</h3>
            {pre && <p className="text-xs text-navy-500">{pre.enfant_prenom} {pre.enfant_nom}{pre.structure_nom ? ` · ${pre.structure_nom}` : ''}</p>}
          </div>
          <div className="flex items-center gap-2">
            {msg && <span className={`text-xs ${msg.startsWith('Erreur') ? 'text-rose-600' : 'text-emerald-600'}`}>{msg}</span>}
            <button onClick={saveAll} disabled={saving || loading} className="btn-primary flex items-center gap-1.5 text-sm">
              {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
          </div>
        </div>

        {loading || !pre ? (
          <div className="py-16 text-center text-gray-400">Chargement…</div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Suivi */}
            <Section icon={CalendarDays} title="Suivi du dossier"
              right={
                <select value={pre.statut || 'demande'} onChange={e => updPre('statut', e.target.value)} className="form-select text-xs py-1">
                  {STATUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              }>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Demande (site)"><input type="date" className="form-input text-sm" value={pre.date_demande_site || ''} onChange={e => updPre('date_demande_site', e.target.value)} /></Field>
                <Field label="Devis"><input type="date" className="form-input text-sm" value={pre.date_devis || ''} onChange={e => updPre('date_devis', e.target.value)} /></Field>
                <Field label="Devis signé"><input type="date" className="form-input text-sm" value={pre.date_devis_signe || ''} onChange={e => updPre('date_devis_signe', e.target.value)} /></Field>
                <Field label="Convention signée"><input type="date" className="form-input text-sm" value={pre.date_convention_signee || ''} onChange={e => updPre('date_convention_signee', e.target.value)} /></Field>
                <Field label="Chèques reçus"><input type="date" className="form-input text-sm" value={pre.date_reception_cheques || ''} onChange={e => updPre('date_reception_cheques', e.target.value)} /></Field>
                <Field label="N° chèques"><input className="form-input text-sm" value={pre.numeros_cheques || ''} onChange={e => updPre('numeros_cheques', e.target.value)} /></Field>
              </div>
            </Section>

            {/* Échange téléphonique */}
            <Section icon={Phone} title="Échange téléphonique">
              <div className="grid sm:grid-cols-[180px_1fr] gap-3">
                <Field label="Date de l'appel"><input type="date" className="form-input text-sm" value={pre.date_appel || ''} onChange={e => updPre('date_appel', e.target.value)} /></Field>
                <Field label="Notes de l'appel"><textarea rows={3} className="form-input text-sm" placeholder="Besoin exprimé, jours souhaités, questions, impressions…" value={pre.notes_appel || ''} onChange={e => updPre('notes_appel', e.target.value)} /></Field>
              </div>
            </Section>

            {/* Visite */}
            <Section icon={Home} title="Visite">
              <div className="grid sm:grid-cols-[180px_1fr] gap-3">
                <Field label="Date de la visite"><input type="date" className="form-input text-sm" value={pre.date_visite || ''} onChange={e => updPre('date_visite', e.target.value)} /></Field>
                <Field label="Notes de la visite"><textarea rows={3} className="form-input text-sm" placeholder="Déroulé de la visite, points abordés, décision…" value={pre.notes_visite || ''} onChange={e => updPre('notes_visite', e.target.value)} /></Field>
              </div>
            </Section>

            {/* Planning souhaité */}
            <Section icon={CalendarDays} title="Planning souhaité">
              <Field label="Jours & horaires souhaités"><input className="form-input text-sm" placeholder="ex : Lundi 8h-18h, Mardi 8h-18h…" value={pre.jours_souhaites || ''} onChange={e => updPre('jours_souhaites', e.target.value)} /></Field>
            </Section>

            {/* Enfant & famille */}
            {enfant ? (
              <Section icon={Baby} title="Enfant & famille">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Prénom"><input className="form-input text-sm" value={enfant.prenom || ''} onChange={e => updE('prenom', e.target.value)} /></Field>
                  <Field label="Nom"><input className="form-input text-sm" value={enfant.nom || ''} onChange={e => updE('nom', e.target.value)} /></Field>
                  <Field label="Date de naissance"><input type="date" className="form-input text-sm" value={(enfant.date_naissance || '').slice(0, 10)} onChange={e => updE('date_naissance', e.target.value)} /></Field>
                  <Field label="Sexe">
                    <select className="form-select text-sm" value={enfant.sexe || ''} onChange={e => updE('sexe', e.target.value)}>
                      <option value="">—</option><option value="F">Fille</option><option value="M">Garçon</option>
                    </select>
                  </Field>
                  <Field label="Date d'entrée prévue"><input type="date" className="form-input text-sm" value={(enfant.date_entree_prevue || '').slice(0, 10)} onChange={e => updE('date_entree_prevue', e.target.value)} /></Field>
                  <Field label="Adresse"><input className="form-input text-sm" value={enfant.adresse || ''} onChange={e => updE('adresse', e.target.value)} /></Field>
                </div>

                {[{ p: p1, set: setP1, t: 'Parent 1' }, { p: p2, set: setP2, t: 'Parent 2' }].map(({ p, set, t }, i) => (
                  <div key={i} className="mt-3 pt-3 border-t border-cream-200">
                    <div className="text-xs font-semibold text-navy-500 mb-2">{t}</div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <Field label="Prénom"><input className="form-input text-sm" value={p.prenom || ''} onChange={e => set(v => ({ ...v, prenom: e.target.value }))} /></Field>
                      <Field label="Nom"><input className="form-input text-sm" value={p.nom || ''} onChange={e => set(v => ({ ...v, nom: e.target.value }))} /></Field>
                      <Field label="Lien">
                        <select className="form-select text-sm" value={p.lien || ''} onChange={e => set(v => ({ ...v, lien: e.target.value }))}>
                          <option value="">—</option><option>Mère</option><option>Père</option><option>Tuteur</option><option>Autre</option>
                        </select>
                      </Field>
                      <Field label="E-mail"><input type="email" className="form-input text-sm" value={p.email || ''} onChange={e => set(v => ({ ...v, email: e.target.value }))} /></Field>
                      <Field label="Téléphone"><input className="form-input text-sm" value={p.telephone_portable || ''} onChange={e => set(v => ({ ...v, telephone_portable: e.target.value }))} /></Field>
                      <Field label="Profession"><input className="form-input text-sm" value={p.profession || ''} onChange={e => set(v => ({ ...v, profession: e.target.value }))} /></Field>
                    </div>
                  </div>
                ))}
              </Section>
            ) : (
              <Section icon={Baby} title="Enfant & famille">
                <p className="text-sm text-navy-400">Aucune fiche enfant n'est rattachée à cette pré-inscription (ou accès réservé à la direction).</p>
              </Section>
            )}

            {/* Convention de réservation */}
            <Section icon={FileSignature} title="Convention de réservation">
              {enfant?.id ? (
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={telechargerConvention} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-navy-200 text-navy-700 hover:bg-navy-50">
                    <Download size={15} /> Télécharger la convention (PDF)
                  </button>
                  <EnvoiDocumentButton type="convention" enfantId={enfant.id} refId={enfant.id} destinataireDefaut={p1?.email} label="Envoyer à la famille" />
                  <SignatureButton type="convention" refId={enfant.id} email={p1?.email} nom={`${p1?.prenom || ''} ${p1?.nom || ''}`.trim()} />
                  <span className="text-xs text-navy-400 w-full sm:w-auto">Pré-remplie avec les informations ci-dessus. Pensez à <strong>Enregistrer</strong> avant de générer.</span>
                </div>
              ) : (
                <p className="text-sm text-navy-400">Rattachez une fiche enfant pour générer la convention.</p>
              )}
            </Section>

            {/* Suivi des e-mails (IMAP contact@lescachettes.fr, rattachés par e-mail du parent) */}
            {enfant?.id && (
              <Section icon={Mail} title="Suivi des e-mails">
                {!mails ? (
                  <p className="text-sm text-navy-300">Chargement…</p>
                ) : !mails.configured ? (
                  <p className="text-sm text-navy-400">Le relevé des e-mails n'est pas activé.</p>
                ) : !(mails.mails || []).length ? (
                  <p className="text-sm text-navy-400">Aucun e-mail rattaché à cette famille{(!p1?.email && !p2?.email) ? ' (renseignez l\'e-mail d\'un parent).' : '.'}</p>
                ) : (
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {mails.mails.map(m => (
                      <div key={m.id} className="flex items-start gap-2 text-xs border-b border-cream-200 pb-1.5">
                        {m.direction === 'recu'
                          ? <ArrowDownLeft size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" title="Reçu" />
                          : <ArrowUpRight size={13} className="text-blue-500 mt-0.5 flex-shrink-0" title="Envoyé" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-navy-800 font-medium truncate">{m.sujet || '(sans objet)'}</div>
                          {m.extrait && <div className="text-navy-400 truncate">{m.extrait}</div>}
                        </div>
                        <div className="text-navy-400 whitespace-nowrap">{(m.date_mail || '').slice(0, 10)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
